import { apiClient, type ManualCommandConfig } from '@/services/http/client'

export const root = '/legal-control-policies'
export const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
export const digest = /^[0-9a-f]{64}$/
export const actions = [
  'profile.read',
  'profile.update',
  'document.read',
  'document.download',
  'document.upload',
  'output.read',
  'output.download',
  'output.publish',
  'firm_access.manage',
  'client_switch.manage',
]
export type Data = Record<string, unknown>
export function object(value: unknown): Data {
  if (!value || typeof value !== 'object' || Array.isArray(value))
    throw new Error('Original receipt unavailable')
  return value as Data
}
export function id(value: unknown): string {
  if (typeof value !== 'string' || !uuid.test(value))
    throw new Error('Use the original record reference')
  return value.toLowerCase()
}
export function future(value: unknown): boolean {
  return (
    typeof value === 'string' &&
    Number.isFinite(Date.parse(value)) &&
    Date.parse(value) > Date.now()
  )
}
export function scopedActions(value: unknown): string[] {
  if (
    !Array.isArray(value) ||
    !value.length ||
    value.length > 10 ||
    value.some(v => typeof v !== 'string' || !actions.includes(v)) ||
    new Set(value).size !== value.length
  )
    throw new Error('Exact reviewed actions unavailable')
  return value as string[]
}
export interface Terms extends Data {
  client_id: string
  subject_id: string
  proposed_account_id: string
  subject_type: 'HUF' | 'business'
  requested_role: 'legal_controller' | 'representative'
  declared_capacity: string
  requested_actions: string[]
  authority_seconds: number
}
export interface Offer {
  review_id: string
  terms_sha256: string
  expires_at: string
  terms: Terms
}
export function offer(value: unknown, review: string, account: string): Offer {
  const row = object(value),
    terms = object(row.terms)
  if (
    row.authority !== 'historically_reviewed_terms_only' ||
    row.authority_granted !== false ||
    row.controller_admitted !== false ||
    id(row.review_id) !== id(review) ||
    id(terms.proposed_account_id) !== id(account) ||
    !(
      (terms.subject_type === 'HUF' &&
        (terms.requested_role === 'legal_controller' ||
          terms.requested_role === 'representative')) ||
      (terms.subject_type === 'business' &&
        (terms.requested_role === 'legal_controller' || terms.requested_role === 'representative'))
    ) ||
    typeof row.terms_sha256 !== 'string' ||
    !digest.test(row.terms_sha256) ||
    typeof row.expires_at !== 'string' ||
    !Number.isFinite(Date.parse(row.expires_at)) ||
    typeof terms.declared_capacity !== 'string' ||
    !Number.isInteger(terms.authority_seconds) ||
    Number(terms.authority_seconds) < 1
  )
    throw new Error('Exact reviewed appointment terms unavailable')
  id(terms.subject_id)
  id(terms.client_id)
  scopedActions(terms.requested_actions)
  return row as unknown as Offer
}
export type Kind =
  | 'review'
  | 'prepare'
  | 'reserve'
  | 'issue'
  | 'cancel'
  | 'reconcile'
  | 'project'
  | 'revoke'
  | 'abort'
  | 'accept'
  | 'withdraw'
const kinds: Kind[] = [
  'review',
  'prepare',
  'reserve',
  'issue',
  'cancel',
  'reconcile',
  'project',
  'revoke',
  'abort',
  'accept',
  'withdraw',
]
export interface Reference {
  kind: Kind
  target: string
  command: string
  status: 'pending' | 'confirmed'
  preparation?: { review: string; acceptance: string; ordinal: number }
}
function preparationInputs(value: unknown): NonNullable<Reference['preparation']> {
  const data = object(value)
  if (
    Object.keys(data).sort().join(',') !== 'acceptance,ordinal,review' ||
    !Number.isInteger(data.ordinal) ||
    Number(data.ordinal) < 1 ||
    Number(data.ordinal) > 2147483647
  )
    throw new Error('Exact original preparation references required')
  return { review: id(data.review), acceptance: id(data.acceptance), ordinal: Number(data.ordinal) }
}
export function bindPreparation(ref: Reference, body: Data): Reference {
  if (
    ref.kind !== 'prepare' ||
    Object.keys(body).sort().join(',') !==
      'acceptance_id,expected_ordinal,intent_id,request_id,review_id' ||
    id(body.intent_id) !== id(ref.command) ||
    id(body.request_id) !== id(ref.target)
  )
    throw new Error('Exact original preparation references required')
  return {
    ...ref,
    preparation: preparationInputs({
      review: body.review_id,
      acceptance: body.acceptance_id,
      ordinal: body.expected_ordinal,
    }),
  }
}
export function preparationBody(ref: Reference): Data {
  if (ref.kind !== 'prepare' || !ref.preparation)
    throw new Error('Saved original preparation references unavailable')
  const p = preparationInputs(ref.preparation)
  return {
    intent_id: id(ref.command),
    request_id: id(ref.target),
    review_id: p.review,
    acceptance_id: p.acceptance,
    expected_ordinal: p.ordinal,
  }
}
const key = (account: string) => 'taxhive.legal-workflow.v1:' + id(account)
export function history(account: string): Reference[] {
  const raw = localStorage.getItem(key(account))
  if (!raw) return []
  const data: unknown = JSON.parse(raw)
  if (!Array.isArray(data) || data.length > 100)
    throw new Error('Original command history unavailable')
  return data.map(value => {
    const r = object(value)
    if (!kinds.includes(r.kind as Kind) || !['pending', 'confirmed'].includes(String(r.status)))
      throw new Error('Original command history unavailable')
    return {
      kind: r.kind as Kind,
      target: id(r.target),
      command: id(r.command),
      status: r.status as Reference['status'],
      ...(r.preparation === undefined
        ? {}
        : {
            preparation:
              r.kind === 'prepare'
                ? preparationInputs(r.preparation)
                : (() => {
                    throw new Error('Preparation references on wrong operation')
                  })(),
          }),
    }
  })
}
export function remember(account: string, ref: Reference): void {
  id(ref.target)
  id(ref.command)
  const rows = history(account)
  if (ref.preparation !== undefined) {
    if (ref.kind !== 'prepare') throw new Error('Preparation references on wrong operation')
    ref = { ...ref, preparation: preparationInputs(ref.preparation) }
  }
  const prior = rows.find(r => r.kind === ref.kind && r.command === ref.command)
  if (prior && JSON.stringify(prior.preparation) !== JSON.stringify(ref.preparation))
    throw new Error('Original preparation references differ')
  const dispatchKinds: Kind[] = ['reserve', 'issue', 'cancel']
  if (
    rows.some(
      r =>
        r.command === ref.command &&
        (r.target !== ref.target ||
          (r.kind !== ref.kind &&
            !(dispatchKinds.includes(r.kind) && dispatchKinds.includes(ref.kind))))
    )
  )
    throw new Error('Original command differs')
  const rest = rows.filter(r => r.command !== ref.command || r.kind !== ref.kind)
  const updated = [
    ref,
    ...rest.filter(r => r.status === 'pending'),
    ...rest.filter(r => r.status === 'confirmed').slice(0, 20),
  ]
  if (updated.length > 100)
    throw new Error('Resolve command history before creating another command')
  const encoded = JSON.stringify(updated)
  localStorage.setItem(key(account), encoded)
  if (localStorage.getItem(key(account)) !== encoded)
    throw new Error('Original command reference was not saved')
}
export function confirm(account: string, ref: Reference): void {
  remember(account, { ...ref, status: 'confirmed' })
}
const intentPath = (intent: string) => `${root}/admission-preparations/${id(intent)}`
export function endpoint(ref: Reference, recovery = false): string {
  const target = id(ref.target),
    command = id(ref.command)
  if (ref.kind === 'review')
    return recovery ? `${root}/reviews/${command}` : `${root}/review-intakes/${target}/reviews`
  if (ref.kind === 'prepare')
    return recovery
      ? intentPath(command)
      : `${root}/review-intakes/${target}/admission-preparations`
  if (ref.kind === 'accept')
    return recovery
      ? `/auth/client/legal-review-acceptance-commands/${command}`
      : `/auth/client/legal-control-reviews/${target}/accept`
  if (ref.kind === 'withdraw')
    return `/auth/client/legal-control-consents/${target}` + (recovery ? '' : '/withdraw')
  const suffix: Record<string, string> = recovery
    ? {
        reserve: 'source-reservation',
        issue: 'admission-decision',
        cancel: 'admission-decision',
        reconcile: 'operator-terminal',
        project: 'controller',
        revoke: 'controller',
        abort: 'operator-preparation',
      }
    : {
        reserve: 'reserve-source',
        issue: 'decide-admission',
        cancel: 'decide-admission',
        reconcile: 'operator-reconcile-terminal',
        project: 'project-controller',
        revoke: 'revoke-controller',
        abort: 'operator-abort',
      }
  return intentPath(target) + (suffix[ref.kind] ? '/' + suffix[ref.kind] : '')
}
export function receipt(value: unknown, ref: Reference, account?: string): Data {
  const row = object(value)
  if (ref.kind === 'review') {
    if (
      id(row.review_id) !== id(ref.command) ||
      row.authority !== 'historical_human_review_only' ||
      row.authority_granted !== false ||
      row.controller_admitted !== false
    )
      throw new Error('Original review unavailable')
  } else if (ref.kind === 'accept') {
    const consent = object(row.consent),
      terms = object(consent.terms)
    if (
      id(row.command_id) !== id(ref.command) ||
      id(row.review_id) !== id(ref.target) ||
      row.authority !== 'reviewed_account_acceptance_only' ||
      row.authority_granted !== false ||
      row.controller_admitted !== false ||
      (account && id(terms.proposed_account_id) !== id(account))
    )
      throw new Error('Original acceptance unavailable')
    id(row.acceptance_id)
    id(consent.consent_id)
  } else if (ref.kind === 'withdraw') {
    if (
      id(row.consent_id) !== id(ref.target) ||
      row.state !== 'withdrawn' ||
      row.authority_granted !== false
    )
      throw new Error('Consent withdrawal remains pending')
  } else if (ref.kind === 'project' || ref.kind === 'revoke') {
    if (
      id(row.intent_id) !== id(ref.target) ||
      row.authority !== 'governed_huf_controller_projection' ||
      !['legal_controller', 'representative'].includes(String(row.role)) ||
      !['active', 'expired', 'revoked'].includes(String(row.state)) ||
      (ref.kind === 'project' && id(row.command_id) !== id(ref.command)) ||
      (ref.kind === 'revoke' &&
        (row.state !== 'revoked' || id(row.revocation_command_id) !== id(ref.command)))
    )
      throw new Error('Original appointment unavailable')
    id(row.client_id)
    id(row.account_id)
    scopedActions(row.actions)
    if (typeof row.expires_at !== 'string' || !Number.isFinite(Date.parse(row.expires_at)))
      throw new Error('Appointment expiry unavailable')
  } else if (ref.kind === 'issue' || ref.kind === 'cancel' || ref.kind === 'reserve') {
    if (
      ref.kind === 'reserve' &&
      (row.authority !== 'tax_source_reservation_only' || row.authority_granted !== false)
    )
      throw new Error('Original source receipt unavailable')
    if (
      id(object(row.command).intent_id) !== id(ref.target) ||
      !['reserved', 'issued', 'no_effect'].includes(String(row.state))
    )
      throw new Error('Original source outcome unavailable')
    if (
      ref.kind !== 'reserve' &&
      (id(row.decision_id) !== id(ref.command) ||
        row.authority !== 'security_legal_admission_terminal')
    )
      throw new Error('Original decision unavailable')
  } else {
    if (
      row.authority !==
      (ref.kind === 'reconcile'
        ? 'admission_terminal_evidence_only'
        : 'local_admission_preparation_only')
    )
      throw new Error('Original preparation receipt unavailable')
    if (
      id(row.intent_id) !== id(ref.kind === 'prepare' ? ref.command : ref.target) ||
      row.authority_granted !== false ||
      row.controller_admitted !== false
    )
      throw new Error('Original preparation outcome unavailable')
    if (ref.kind === 'reconcile' && id(row.command_id) !== id(ref.command))
      throw new Error('Original reconciliation differs')
    if (ref.kind === 'abort' && row.state !== 'aborted')
      throw new Error('Preparation release remains pending')
  }
  return row
}
export async function submit(ref: Reference, body: Data): Promise<unknown> {
  const config: ManualCommandConfig = { manualCommand: true }
  return (await apiClient.post(endpoint(ref), body, config)).data
}
export async function recover(ref: Reference): Promise<unknown> {
  return (await apiClient.get(endpoint(ref, true))).data
}
export async function inspectIntake(request: string, ordinal: number): Promise<Data> {
  const row = object(
    (
      await apiClient.get(`${root}/review-intakes/${id(request)}/current-source`, {
        params: { expected_ordinal: ordinal },
      })
    ).data
  )
  if (
    id(row.request_id) !== id(request) ||
    row.ordinal !== ordinal ||
    row.authority !== 'intake_current_source_observation_only' ||
    row.current_source_verified !== true ||
    row.authority_granted !== false ||
    row.subject_type !== 'HUF' ||
    !['legal_controller', 'representative'].includes(String(row.requested_role))
  )
    throw new Error('Current HUF intake unavailable')
  scopedActions(row.requested_actions)
  return row
}
export async function readOffer(review: string, account: string): Promise<Offer> {
  return offer(
    (await apiClient.get(`/auth/client/legal-control-reviews/${id(review)}`)).data,
    review,
    account
  )
}

export interface AcceptanceReferencePage {
  review_id: string
  items: Array<{
    acceptance_id: string
    command_id: string
    consent_id: string
    accepted_at: string
  }>
  next_cursor: string | null
}
export async function acceptanceReferences(
  review: string,
  after?: string
): Promise<AcceptanceReferencePage> {
  const response = await apiClient.get(
    `/auth/client/legal-control-reviews/${id(review)}/acceptances`,
    after ? { params: { after: id(after) } } : undefined
  )
  const page = object(response.data)
  if (
    id(page.review_id) !== id(review) ||
    page.authority !== 'account_acceptance_reference_history_only' ||
    page.authority_granted !== false ||
    !Array.isArray(page.items) ||
    page.items.length > 20
  )
    throw new Error('Exact acceptance history unavailable')
  const seen = new Set<string>()
  const items = page.items.map(value => {
    const row = object(value)
    const acceptance = id(row.acceptance_id)
    if (
      seen.has(acceptance) ||
      typeof row.accepted_at !== 'string' ||
      !Number.isFinite(Date.parse(row.accepted_at))
    )
      throw new Error('Exact acceptance history unavailable')
    seen.add(acceptance)
    return {
      acceptance_id: acceptance,
      command_id: id(row.command_id),
      consent_id: id(row.consent_id),
      accepted_at: row.accepted_at,
    }
  })
  const next = page.next_cursor === null ? null : id(page.next_cursor)
  if (next && (items.length !== 20 || next !== items[items.length - 1].acceptance_id))
    throw new Error('Exact acceptance history cursor required')
  return { review_id: id(page.review_id), items, next_cursor: next }
}

export async function operatorPreparation(intent: string): Promise<Data> {
  const row = object((await apiClient.get(intentPath(intent) + '/operator-preparation')).data)
  if (
    id(row.intent_id) !== id(intent) ||
    row.authority !== 'local_admission_preparation_only' ||
    row.authority_granted !== false ||
    row.controller_admitted !== false ||
    !['prepared', 'aborted'].includes(String(row.state)) ||
    typeof row.intake_frozen !== 'boolean'
  )
    throw new Error('Exact preparation state unavailable')
  return row
}
